import { getMessage } from '@/localization.js';
import messages from './localization.json';

export class FormValidationController {
    constructor(host, options = {}) {
        this.host = host;
        this.getValue = options.getValue || (() => '');
        this.getInputRef = options.getInputRef || (() => null);
        this._errorMessage = '';
        this._isFirstValidity = true;
        this._isValid = true;

        if (!host._internals) {
            host._internals = host.attachInternals?.();
        }

        this._internals = host._internals;
    }

    getErrorMessage() {
        return this._errorMessage;
    }

    setInputHandlers() {
        this.host?.form?.addEventListener('submit', this._handleFormSubmit);
        this.host?.addEventListener('invalid', this._handleInvalid);
    }

    removeInputHandlers = () => {
        this.host?.form?.removeEventListener('submit', this._handleFormSubmit);
        this.host?.removeEventListener('invalid', this._handleInvalid);
    };

    setValidateState() {
        this.host.toggleAttribute('error', !this.validate());
        this.host.setAttribute('error-text', this._errorMessage);
    }

    _handleInvalid = (e) => {
        e.preventDefault();
        this.setValidateState();

        if (this.host.form) {
            this._focusTo(this._getInvalidItem());
        } else if (!this._isValid) {
            this._focusTo(e.target);
        }
    };

    _getInvalidItem() {
        return this.host.form.querySelector(':invalid') || this.host.form.querySelector('[error]');
    }

    _focusTo(item) {
        if (!item) {
            return;
        }

        item?.focus({ preventScroll: false });
        const scrollOptions = { behavior: 'auto', block: 'start' };
        item.scrollIntoView(scrollOptions);
    }

    _handleFormSubmit = (e) => {
        if (!e.cancelable) {
            return;
        }

        this.setValidateState();

        if (this.host.form) {
            const invalidItem = this._getInvalidItem();

            if (invalidItem) {
                e.preventDefault();
                this._focusTo(invalidItem);
            }
        } else if (!this._isValid) {
            e.preventDefault();
            this._focusTo(e.target);
        }
    };

    _validateMinLength() {
        const inputRef = this.getInputRef();
        const minInputLength =
            !inputRef.value?.minLength || inputRef.value?.minLength === -1 ? 0 : inputRef.value?.minLength;

        if (inputRef.value?.value?.length >= minInputLength) {
            return true;
        }

        const errorMessage = getMessage(messages, 'invalidInputTooShort', {
            '#LENGTH#': inputRef.value?.minLength,
        });

        this._internals.setValidity({ tooShort: true }, errorMessage);
        this._errorMessage = errorMessage;
        return false;
    }

    _validateMaxLength() {
        const inputRef = this.getInputRef();
        const maxInputLength =
            !inputRef.value?.maxLength || inputRef.value?.maxLength === -1 ? Infinity : inputRef.value?.maxLength;

        if (inputRef.value?.value?.length <= maxInputLength) {
            return true;
        }

        const errorMessage = getMessage(messages, 'invalidInputTooLong', {
            '#LENGTH#': inputRef.value?.maxLength,
        });

        this._internals.setValidity({ tooLong: true }, errorMessage);
        this._errorMessage = errorMessage;

        return false;
    }

    validate() {
        const inputRef = this.getInputRef();

        if (!inputRef?.value || !this._internals) {
            return (this._isValid = true);
        }

        if (!inputRef.value.checkValidity()) {
            this._errorMessage = inputRef.value.validationMessage;
            this._internals.setValidity(inputRef.value.validity, this._errorMessage, inputRef?.value);

            return (this._isValid = false);
        }

        if (!this._validateMinLength()) {
            return (this._isValid = false);
        }

        if (!this._validateMaxLength()) {
            return (this._isValid = false);
        }

        this._errorMessage = '';
        this._internals.setValidity({});

        return (this._isValid = true);
    }
}
